/**
 * Execution Cockpit Daily Brief V6 - Main Component
 *
 * Ported from: docs/ui-concepts/homepage-cockpit/helix-execution-cockpit-v6.jsx
 *
 * Design System: HELIX Professional Design System v9.2.1 (Executive OS)
 *
 * EXECUTIVE READABILITY PASS:
 * - Body text: 14-15px for decision-grade content
 * - Secondary text: 13px minimum
 * - Labels/captions: 11-12px (never smaller)
 * - Micro text: 11px (rare use, never for decisions)
 * - Line height: 1.5 for comfortable reading
 */

'use client';

import React, { useEffect, useState, useMemo } from 'react';
import {
  AlertTriangle,
  Activity,
  Briefcase,
  UserX,
  Shield,
  Target,
  Calendar,
  Building2,
  Search,
  FileText,
  BarChart3,
  TrendingUp,
  Layers,
  GitMerge,
  Users,
  UserPlus,
  SlidersHorizontal,
  Zap,
  CheckCircle2,
  ExternalLink,
  ChevronRight,
  Filter,
  Minus,
  X,
} from 'lucide-react';
import { c, evidenceStatusColors } from './tokens';
import {
  RAGDot,
  ConfidenceBadge,
  Dropdown,
  LensChip,
  RiskRadarTile,
  DecisionCard,
  BetCard,
  DecisionOptionBtn,
  ProofAnchor,
  PriorityDriverChip,
} from './primitives';
import {
  formatExecutiveAccountabilityLabel,
  formatExecutiveOwnerLabel,
  formatExecutiveStrategyScopeLabel,
} from '@/lib/executive-safe-format';
import type {
  ExecutionCockpitDailyBriefDto,
  ExecutionCockpitDailyBriefMetaDto,
  ExecutionCockpitDecisionPackageActivateRequestDto,
  ExecutionCockpitDecisionPackageDecisionDto,
  ExecutionCockpitDecisionPackageDto,
  ExecutionCockpitDecisionPackageReviewHistoryResponseDto,
  ExecutionCockpitDecisionPackageReviewRequestDto,
} from '@helix/lib';

// ═══════════════════════════════════════════════════════════════════════════
// ICON MAPPING FOR RISK RADAR TILES
// ═══════════════════════════════════════════════════════════════════════════
const riskRadarIcons: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  strategic_bets_at_risk: AlertTriangle,
  role_decisions_pending: Briefcase,
  hiring_risk_delay: UserX,
  guardrail_violations: Shield,
  org_drift_alerts: Activity,
};

// ═══════════════════════════════════════════════════════════════════════════
// ICON MAPPING FOR DECISION OPTIONS BY KIND
// ═══════════════════════════════════════════════════════════════════════════
const optionKindIcons: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  reallocate: Users,
  vendor: UserPlus,
  scope: SlidersHorizontal,
  time: Calendar,
  do_nothing: Minus,
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER: Format date string
// ═══════════════════════════════════════════════════════════════════════════
function formatAsOfDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return isoString;
  }
}

function formatShortDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoString;
  }
}

type DraftDecisionTargetsForm = {
  targetRag: string;
  maxSlipWeeks: string;
  maxGapPoints: string;
  maxStalledCount: string;
};

type DraftDecisionDetails = {
  ownerUserId: string;
  dueDate: string;
  rationale: string;
  reviewCadence: string;
  successCriteria: string;
  targets: DraftDecisionTargetsForm;
};

function emptyDraftDecisionDetails(): DraftDecisionDetails {
  return {
    ownerUserId: '',
    dueDate: '',
    rationale: '',
    reviewCadence: 'weekly',
    successCriteria: '',
    targets: { targetRag: '', maxSlipWeeks: '', maxGapPoints: '', maxStalledCount: '' },
  };
}

function formatStructuredTargetsFromDraft(targets: DraftDecisionTargetsForm): string | null {
  const parts: string[] = [];
  const rag = String(targets.targetRag ?? '').trim().toLowerCase();
  if (rag) parts.push(`RAG ≥ ${rag.toUpperCase()}`);
  const slip = String(targets.maxSlipWeeks ?? '').trim();
  if (slip) parts.push(`Slip ≤ ${slip}w`);
  const gap = String(targets.maxGapPoints ?? '').trim();
  if (gap) parts.push(`Gap ≤ ${gap} pts`);
  const stalled = String(targets.maxStalledCount ?? '').trim();
  if (stalled) parts.push(`Stalled ≤ ${stalled}`);
  return parts.length ? parts.join(' • ') : null;
}

function formatStructuredTargetsFromDto(
  targets: ExecutionCockpitDecisionPackageDecisionDto['targets'] | null | undefined,
): string | null {
  if (!targets) return null;
  const parts: string[] = [];
  if (targets.targetRag) parts.push(`RAG ≥ ${String(targets.targetRag).toUpperCase()}`);
  if (targets.maxSlipWeeks != null) parts.push(`Slip ≤ ${targets.maxSlipWeeks}w`);
  if (targets.maxGapPoints != null) parts.push(`Gap ≤ ${targets.maxGapPoints} pts`);
  if (targets.maxStalledCount != null) parts.push(`Stalled ≤ ${targets.maxStalledCount}`);
  return parts.length ? parts.join(' • ') : null;
}

function computeNextReviewDueIso(params: { referenceAt: string; cadence: string | null }): string | null {
  const cadence = String(params.cadence ?? '').trim().toLowerCase();
  if (cadence !== 'weekly' && cadence !== 'biweekly' && cadence !== 'monthly') return null;
  const referenceAt = new Date(params.referenceAt);
  if (Number.isNaN(referenceAt.getTime())) return null;

  const addDaysUtc = (date: Date, days: number): Date => {
    const out = new Date(date.getTime());
    out.setUTCDate(out.getUTCDate() + days);
    return out;
  };

  const addMonthsClampedUtc = (date: Date, months: number): Date => {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth();
    const d = date.getUTCDate();
    const h = date.getUTCHours();
    const min = date.getUTCMinutes();
    const s = date.getUTCSeconds();
    const ms = date.getUTCMilliseconds();

    const targetMonthIndex = m + months;
    const targetYear = y + Math.floor(targetMonthIndex / 12);
    const targetMonth = targetMonthIndex % 12;
    const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    const clampedDay = Math.min(d, daysInTargetMonth);

    return new Date(Date.UTC(targetYear, targetMonth, clampedDay, h, min, s, ms));
  };

  const addInterval = (date: Date): Date => {
    if (cadence === 'weekly') return addDaysUtc(date, 7);
    if (cadence === 'biweekly') return addDaysUtc(date, 14);
    return addMonthsClampedUtc(date, 1);
  };

  return addInterval(referenceAt).toISOString();
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export interface ExecutionCockpitDailyBriefV6Props {
  data: ExecutionCockpitDailyBriefDto;
  meta?: ExecutionCockpitDailyBriefMetaDto | null;
  ownerCandidates?: Array<{ userId: string; label: string; email: string }> | null;
  audience?: 'exec' | 'operator';
  isWriteLocked?: boolean;
  onExportBoardMemo?: () => void;
  isExportingBoardMemo?: boolean;
  onRefreshSnapshot?: () => void;
  isRefreshingSnapshot?: boolean;
  decisionPackage?: ExecutionCockpitDecisionPackageDto | null;
  decisionPackageDecisions?: ExecutionCockpitDecisionPackageDecisionDto[];
  onActivateDecisionPackage?: (
    decisions: ExecutionCockpitDecisionPackageActivateRequestDto['decisions'],
  ) => void | Promise<void>;
  isActivatingDecisionPackage?: boolean;
  onSaveDecisionPackageDraft?: (
    decisions: ExecutionCockpitDecisionPackageActivateRequestDto['decisions'],
  ) => void | Promise<void>;
  isSavingDecisionPackageDraft?: boolean;
  onStartDecisionPackageAmendment?: () => void | Promise<void>;
  isStartingDecisionPackageAmendment?: boolean;
  onReviewDecisionPackage?: (review: ExecutionCockpitDecisionPackageReviewRequestDto) => void | Promise<void>;
  isReviewingDecisionPackage?: boolean;
  onLoadDecisionPackageReviewHistory?: (
    initiativeKey: string,
  ) => Promise<ExecutionCockpitDecisionPackageReviewHistoryResponseDto>;
  topSlot?: React.ReactNode;
  links?: {
    orgCanvas?: string | null;
    evidenceCoverage?: string | null;
    executionCockpit?: string | null;
  };
}

export function ExecutionCockpitDailyBriefV6({
  data,
  meta,
  ownerCandidates,
  audience = 'exec',
  isWriteLocked,
  onExportBoardMemo,
  isExportingBoardMemo,
  onRefreshSnapshot,
  isRefreshingSnapshot,
  decisionPackage,
  decisionPackageDecisions,
  onActivateDecisionPackage,
  isActivatingDecisionPackage,
  onSaveDecisionPackageDraft,
  isSavingDecisionPackageDraft,
  onStartDecisionPackageAmendment,
  isStartingDecisionPackageAmendment,
  onReviewDecisionPackage,
  isReviewingDecisionPackage,
  onLoadDecisionPackageReviewHistory,
  topSlot,
  links,
}: ExecutionCockpitDailyBriefV6Props) {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  const [selectedBetKey, setSelectedBetKey] = useState<string>(data.bets[0]?.key ?? '');
  const [selectedDecisionId, setSelectedDecisionId] = useState<string>(
    data.decisionDocket[0]?.id ?? ''
  );
  const [activeRiskFilterId, setActiveRiskFilterId] = useState<string | null>(null);
  const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false);
  const [isProvenanceDialogOpen, setIsProvenanceDialogOpen] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewOutcome, setReviewOutcome] = useState<ExecutionCockpitDecisionPackageReviewRequestDto['outcome']>(null);
  const [reviewNotes, setReviewNotes] = useState<string>('');
  const [reviewHistoryInitiativeKey, setReviewHistoryInitiativeKey] = useState<string | null>(null);
  const [reviewHistoryStatus, setReviewHistoryStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [reviewHistoryItems, setReviewHistoryItems] = useState<
    ExecutionCockpitDecisionPackageReviewHistoryResponseDto['items']
  >([]);
  const [reviewHistoryError, setReviewHistoryError] = useState<string | null>(null);
  const [isAccountabilityPanelOpen, setIsAccountabilityPanelOpen] = useState(audience === 'operator');
  const [draftDecisionSelections, setDraftDecisionSelections] = useState<Record<string, number | null>>(() => {
    const selections: Record<string, number | null> = {};
    const docketKeys = data.decisionDocket
      .map((d) => d.linkedInitiativeKey)
      .filter(Boolean) as string[];
    const betByKey = new Map(data.bets.map((b) => [b.key, b]));
    for (const key of docketKeys) {
      const bet = betByKey.get(key);
      selections[key] = bet?.options?.length ? 0 : null;
    }
    return selections;
  });
  const [draftDecisionDetails, setDraftDecisionDetails] = useState<Record<string, DraftDecisionDetails>>(() => {
    const details: Record<string, DraftDecisionDetails> = {};
    const docketKeys = data.decisionDocket
      .map((d) => d.linkedInitiativeKey)
      .filter(Boolean) as string[];
    for (const key of docketKeys) {
      details[key] = emptyDraftDecisionDetails();
    }
    return details;
  });

  // ─────────────────────────────────────────────────────────────────────────
  // DERIVED DATA
  // ─────────────────────────────────────────────────────────────────────────
  const writeLocked = isWriteLocked === true;
  const visibleBets = useMemo(() => {
    if (!activeRiskFilterId) return data.bets;
    if (activeRiskFilterId === 'strategic_bets_at_risk') {
      return data.bets.filter((b) => b.rag !== 'green');
    }
    if (activeRiskFilterId === 'role_decisions_pending') {
      const docketKeys = new Set(data.decisionDocket.map((d) => d.linkedInitiativeKey).filter(Boolean) as string[]);
      return data.bets.filter((b) => docketKeys.has(b.key));
    }
    if (activeRiskFilterId === 'hiring_risk_delay') {
      return data.bets.filter((b) => b.constraint.toLowerCase().includes('capacity gap'));
    }
    return data.bets;
  }, [activeRiskFilterId, data.bets, data.decisionDocket]);

  const selectedBet = useMemo(
    () =>
      visibleBets.find((b) => b.key === selectedBetKey) ??
      visibleBets[0] ??
      data.bets.find((b) => b.key === selectedBetKey) ??
      data.bets[0],
    [data.bets, selectedBetKey, visibleBets]
  );

  const portfolioRank = useMemo(() => {
    const idx = data.bets.findIndex((b) => b.key === selectedBet.key);
    return idx >= 0 ? idx + 1 : null;
  }, [data.bets, selectedBet.key]);

  const isDecisionPackageActive = decisionPackage?.status === 'active';
  const decisionDocketKeys = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const key of data.decisionDocket.map((d) => d.linkedInitiativeKey)) {
      if (!key) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [data.decisionDocket]);
  const decisionDocketKeySet = useMemo(() => new Set(decisionDocketKeys), [decisionDocketKeys]);

  const activeDecisionSelections = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const d of decisionPackageDecisions ?? []) {
      map.set(d.initiativeKey, d.selectedOptionIndex);
    }
    return map;
  }, [decisionPackageDecisions]);

  const decisionByKey = useMemo(() => {
    const map = new Map<string, ExecutionCockpitDecisionPackageDecisionDto>();
    for (const d of decisionPackageDecisions ?? []) {
      map.set(d.initiativeKey, d);
    }
    return map;
  }, [decisionPackageDecisions]);

  const canEditDecisionPackage =
    writeLocked !== true &&
    isDecisionPackageActive !== true &&
    Boolean(onActivateDecisionPackage || onSaveDecisionPackageDraft);

  const canReviewDecisionPackage = writeLocked !== true && Boolean(onReviewDecisionPackage) && Boolean(meta?.activationBaseline);

  useEffect(() => {
    if (!isReviewDialogOpen) return;
    setReviewOutcome(null);
    setReviewNotes('');
    setReviewHistoryInitiativeKey(null);
    setReviewHistoryStatus('idle');
    setReviewHistoryItems([]);
    setReviewHistoryError(null);
  }, [isReviewDialogOpen]);

  useEffect(() => {
    if (decisionPackage?.status !== 'draft') return;

    const betByKey = new Map(data.bets.map((b) => [b.key, b]));

    const toDateInput = (iso: string | null | undefined) => {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    };

    setDraftDecisionSelections((prev) => {
      const next: Record<string, number | null> = {};
      for (const key of decisionDocketKeys) {
        const bet = betByKey.get(key);
        next[key] = bet?.options?.length ? 0 : null;
      }
      for (const d of decisionPackageDecisions ?? []) {
        if (!decisionDocketKeySet.has(d.initiativeKey)) continue;
        next[d.initiativeKey] = d.selectedOptionIndex ?? null;
      }
      return Object.keys(next).length ? next : prev;
    });

    setDraftDecisionDetails((prev) => {
      const next: Record<string, DraftDecisionDetails> = {};
      for (const key of decisionDocketKeys) {
        next[key] = emptyDraftDecisionDetails();
      }
      for (const d of decisionPackageDecisions ?? []) {
        if (!decisionDocketKeySet.has(d.initiativeKey)) continue;
        const reviewCadence = String(d.reviewCadence ?? '').trim() || 'weekly';
        const targets = d.targets ?? null;
        next[d.initiativeKey] = {
          ownerUserId: String(d.ownerUserId ?? '').trim(),
          dueDate: toDateInput(d.dueAt),
          rationale: String(d.rationale ?? '').trim(),
          reviewCadence,
          successCriteria: String(d.successCriteria ?? '').trim(),
          targets: {
            targetRag: String(targets?.targetRag ?? '').trim(),
            maxSlipWeeks: targets?.maxSlipWeeks == null ? '' : String(targets.maxSlipWeeks),
            maxGapPoints: targets?.maxGapPoints == null ? '' : String(targets.maxGapPoints),
            maxStalledCount: targets?.maxStalledCount == null ? '' : String(targets.maxStalledCount),
          },
        };
      }
      return Object.keys(next).length ? next : prev;
    });
  }, [
    data.bets,
    decisionPackage?.id,
    decisionPackage?.status,
    decisionPackageDecisions,
    decisionDocketKeySet,
    decisionDocketKeys,
  ]);

  const proofBasisSummary = useMemo(() => {
    const counts = { evidenceLinked: 0, evidence: 0, heuristic: 0, assumption: 0 };
    for (const proof of selectedBet.proofs) {
      if (proof.basis === 'evidence') {
        if (proof.href) counts.evidenceLinked += 1;
        else counts.evidence += 1;
      } else if (proof.basis === 'heuristic') {
        counts.heuristic += 1;
      } else {
        counts.assumption += 1;
      }
    }
    const parts = [`Evidence-linked ${counts.evidenceLinked}`];
    if (counts.evidence) parts.push(`Evidence ${counts.evidence}`);
    parts.push(`Heuristic ${counts.heuristic}`);
    if (counts.assumption) parts.push(`Assumption ${counts.assumption}`);
    return parts.join(' • ');
  }, [selectedBet.proofs]);

  const activeRiskTile = useMemo(
    () => data.riskRadar.find((t) => t.id === activeRiskFilterId),
    [data.riskRadar, activeRiskFilterId]
  );

  const ragCounts = data.executiveVerdict.statusCounts;
  const evidenceCounts = useMemo(() => {
    const items = Array.isArray(data.evidenceCoverage) ? data.evidenceCoverage : [];
    const connected = items.filter((i) => i.status === 'connected').length;
    const stale = items.filter((i) => i.status === 'stale').length;
    const notConnected = items.filter((i) => i.status === 'not_connected').length;
    return { connected, stale, notConnected, total: items.length };
  }, [data.evidenceCoverage]);

  const orgBaselineStatus = useMemo(() => {
    const items = Array.isArray(data.evidenceCoverage) ? data.evidenceCoverage : [];
    return items.find((i) => i.id === 'org_baseline')?.status ?? 'not_connected';
  }, [data.evidenceCoverage]);

  const orgBaselineDetail = useMemo(() => {
    const items = Array.isArray(data.evidenceCoverage) ? data.evidenceCoverage : [];
    return items.find((i) => i.id === 'org_baseline')?.detail ?? null;
  }, [data.evidenceCoverage]);

  const workSignalsStatus = useMemo(() => {
    const items = Array.isArray(data.evidenceCoverage) ? data.evidenceCoverage : [];
    return items.find((i) => i.id === 'work_signals')?.status ?? 'not_connected';
  }, [data.evidenceCoverage]);

  const workSignalsDetail = useMemo(() => {
    const items = Array.isArray(data.evidenceCoverage) ? data.evidenceCoverage : [];
    return items.find((i) => i.id === 'work_signals')?.detail ?? null;
  }, [data.evidenceCoverage]);

  const hasCapacityGapAssumptions = useMemo(() => {
    return data.bets.some((b) => b.priorityDrivers.some((d) => d.id === 'capacity_gap' && d.basis === 'assumption'));
  }, [data.bets]);

  const showEvidenceGapBanner =
    workSignalsStatus !== 'connected' || (orgBaselineStatus !== 'connected' && hasCapacityGapAssumptions);
  const evidenceGapSeverity = workSignalsStatus === 'not_connected' ? 'critical' : 'warning';

  const computedAtIso = meta?.run?.computedAt ?? data.asOf;
  const asOfLabel = useMemo(() => formatAsOfDate(computedAtIso), [computedAtIso]);

  const sourceKindLabel = useMemo(() => {
    const kind = String(meta?.run?.sourceKind ?? '').trim();
    if (!kind) return '—';
    if (kind === 'jira_snapshot_artifact') return 'Jira work-signal snapshot';
    if (kind === 'github_issues_snapshot') return 'GitHub issue snapshot';
    return 'Approved operating snapshot';
  }, [meta?.run?.sourceKind]);

  const stalenessLabel = useMemo(() => {
    const ms = new Date(computedAtIso).getTime();
    if (!Number.isFinite(ms)) return null;
    const ageMs = Math.max(0, Date.now() - ms);
    const ageHours = ageMs / (1000 * 60 * 60);
    if (ageHours < 24) return null;
    const ageDays = Math.max(1, Math.floor(ageHours / 24));
    return `${ageDays}d old`;
  }, [computedAtIso]);

  const deltaBasisLabel = useMemo(() => {
    if (!meta?.previousRun) return 'Δ n/a';
    if (meta.isComparableToPreviousRun) return `Δ vs ${formatAsOfDate(meta.previousRun.computedAt)}`;
    return 'Δ n/a (not comparable)';
  }, [meta?.isComparableToPreviousRun, meta?.previousRun]);

  const activationBaseline = meta?.activationBaseline ?? null;
  const activationDeltaRows = activationBaseline?.deltas ?? [];

  const ownerCandidateById = useMemo(() => {
    const map = new Map<string, { userId: string; label: string; email: string }>();
    for (const candidate of ownerCandidates ?? []) {
      map.set(candidate.userId, candidate);
    }
    return map;
  }, [ownerCandidates]);
  const ownerCandidateLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const candidate of ownerCandidates ?? []) {
      const userId = String(candidate.userId ?? '').trim();
      if (!userId) continue;
      const label = String(candidate.label ?? '').trim();
      const email = String(candidate.email ?? '').trim();
      map.set(userId, label || email || userId);
    }
    return map;
  }, [ownerCandidates]);

  const draftDecisionsForApi = useMemo(() => {
    const toIsoOrNull = (dateInput: string | null | undefined) => {
      const raw = String(dateInput ?? '').trim();
      if (!raw) return null;
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return null;
      return date.toISOString();
    };

    return decisionDocketKeys.map((initiativeKey) => {
      const details = draftDecisionDetails[initiativeKey] ?? emptyDraftDecisionDetails();
      const ownerUserId = String(details.ownerUserId ?? '').trim() || null;
      const rationale = String(details.rationale ?? '').trim() || null;
      const reviewCadenceRaw = String(details.reviewCadence ?? '').trim().toLowerCase();
      const reviewCadence =
        reviewCadenceRaw === 'weekly' || reviewCadenceRaw === 'biweekly' || reviewCadenceRaw === 'monthly'
          ? (reviewCadenceRaw as 'weekly' | 'biweekly' | 'monthly')
          : null;
      const successCriteria = String(details.successCriteria ?? '').trim() || null;
      const targets = (() => {
        const ragRaw = String(details.targets?.targetRag ?? '').trim().toLowerCase();
        const targetRag = ragRaw === 'green' || ragRaw === 'amber' || ragRaw === 'red' ? ragRaw : null;

        const numOrNull = (value: string) => {
          const raw = String(value ?? '').trim();
          if (!raw) return null;
          const n = Number(raw);
          return Number.isFinite(n) ? n : null;
        };

        const maxSlipWeeks = numOrNull(details.targets?.maxSlipWeeks);
        const maxGapPoints = numOrNull(details.targets?.maxGapPoints);
        const maxStalledCount = numOrNull(details.targets?.maxStalledCount);

        const out: any = {};
        if (targetRag) out.targetRag = targetRag;
        if (maxSlipWeeks != null) out.maxSlipWeeks = maxSlipWeeks;
        if (maxGapPoints != null) out.maxGapPoints = maxGapPoints;
        if (maxStalledCount != null) out.maxStalledCount = maxStalledCount;
        return Object.keys(out).length ? out : null;
      })();
      return {
        initiativeKey,
        selectedOptionIndex: draftDecisionSelections[initiativeKey] ?? null,
        ownerUserId,
        dueAt: toIsoOrNull(details.dueDate),
        rationale,
        reviewCadence,
        successCriteria,
        targets,
      };
    });
  }, [decisionDocketKeys, draftDecisionDetails, draftDecisionSelections]);

  const activationReadiness = useMemo(() => {
    let missingSelections = 0;
    for (const key of decisionDocketKeys) {
      const bet = data.bets.find((b) => b.key === key);
      if (!bet || bet.options.length === 0) continue;
      if (draftDecisionSelections[key] == null) missingSelections += 1;
    }
    return { isReady: missingSelections === 0, missingSelections };
  }, [data.bets, decisionDocketKeys, draftDecisionSelections]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div data-testid="daily-brief-root" style={{ minHeight: '100vh', backgroundColor: c.bg }}>
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* HEADER */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <header
        data-testid="daily-brief-header"
        style={{
          backgroundColor: c.card,
          borderBottom: `1px solid ${c.border}`,
          padding: '8px 20px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            maxWidth: 1480,
            margin: '0 auto',
          }}
        >
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                backgroundColor: c.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BarChart3 style={{ width: 15, height: 15, color: '#fff' }} />
            </div>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'Georgia, serif',
                color: c.text,
              }}
            >
              HELIX
            </span>
          </div>

          <div style={{ width: 1, height: 28, backgroundColor: c.border }} />

          {/* 3 Selectors */}
          <Dropdown icon={Target} label="Strategy" value={data.filters.strategyLabel} width={170} />
          <Dropdown
            icon={Calendar}
            label="Time Window"
            value={data.filters.timeWindowLabel}
            width={130}
          />
          <Dropdown icon={Building2} label="Lens" value={data.filters.lensLabel} width={150} />

          {/* Pinned Lens Chips */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            {data.filters.pinnedLenses.map((lens) => (
              <LensChip
                key={lens.id}
                label={lens.label}
                isActive={data.filters.activePinnedLensId === lens.id}
              />
            ))}
          </div>

          {/* Search (visual only) */}
          {links?.executionCockpit ? (
            <a
              href={links.executionCockpit}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 6,
                marginLeft: 'auto',
                backgroundColor: c.cardMuted,
                border: `1px solid ${c.border}`,
                width: 240,
                textDecoration: 'none',
              }}
              data-testid="daily-brief-open-cockpit"
              title="Open Execution Cockpit"
            >
              <Search style={{ width: 12, height: 12, color: c.textMuted }} />
              <span style={{ fontSize: 12, color: c.textMuted }}>Open Execution Cockpit</span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  padding: '2px 5px',
                  backgroundColor: c.card,
                  border: `1px solid ${c.border}`,
                  borderRadius: 3,
                  color: c.textMuted,
                }}
              >
                ↵
              </span>
            </a>
          ) : (
            <div style={{ marginLeft: 'auto' }} />
          )}

          {/* Timestamp + Export */}
          <div style={{ textAlign: 'right', marginLeft: 12 }}>
            <div style={{ fontSize: 11, color: c.textMuted }}>As of</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: c.textSecondary }}>
              {asOfLabel}
            </div>
            <div
              style={{
                fontSize: 11,
                marginTop: 2,
                color: stalenessLabel ? c.amber : c.textMuted,
              }}
            >
              Source: {sourceKindLabel}
              {stalenessLabel ? ` • Stale (${stalenessLabel})` : ''}
              <button
                type="button"
                onClick={() => setIsProvenanceDialogOpen(true)}
                style={{
                  marginLeft: 8,
                  padding: 0,
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: c.accent,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                View provenance
              </button>
            </div>
            <div
              style={{
                fontSize: 11,
                marginTop: 2,
                color: decisionPackage?.status === 'active' ? c.green : c.textMuted,
              }}
            >
              {(() => {
                const base =
                  decisionPackage?.status === 'active'
                    ? `Active decision package v${decisionPackage.version}`
                    : decisionPackage?.status === 'draft'
                      ? `Draft decision package v${decisionPackage.version} (not activated)`
                      : 'Draft decision package (not saved)';
                return `${base}${isSavingDecisionPackageDraft ? ' • Saving…' : ''}`;
              })()}
            </div>
            {writeLocked ? (
              <div style={{ fontSize: 11, marginTop: 2, color: c.amber }}>
                Read-only access. Write actions are disabled.
              </div>
            ) : null}
          </div>
          <button
            onClick={onRefreshSnapshot}
            disabled={writeLocked || !onRefreshSnapshot || isRefreshingSnapshot === true}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '8px 12px',
              borderRadius: 6,
              backgroundColor: c.cardMuted,
              border: `1px solid ${c.border}`,
              cursor: writeLocked || !onRefreshSnapshot || isRefreshingSnapshot ? 'not-allowed' : 'pointer',
              opacity: writeLocked || !onRefreshSnapshot || isRefreshingSnapshot ? 0.7 : 1,
            }}
          >
            <Zap style={{ width: 12, height: 12, color: c.text }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: c.text }}>
              {isRefreshingSnapshot ? 'Refreshing…' : 'Refresh snapshot'}
            </span>
          </button>
          {!isDecisionPackageActive ? (
            <button
              onClick={() => {
                if (!onSaveDecisionPackageDraft) return;
                void onSaveDecisionPackageDraft(draftDecisionsForApi);
              }}
              disabled={
                writeLocked === true ||
                !onSaveDecisionPackageDraft ||
                isSavingDecisionPackageDraft === true ||
                decisionDocketKeys.length === 0
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '8px 12px',
                borderRadius: 6,
                backgroundColor: c.cardMuted,
                border: `1px solid ${c.border}`,
                cursor:
                  writeLocked === true ||
                  !onSaveDecisionPackageDraft ||
                  isSavingDecisionPackageDraft ||
                  decisionDocketKeys.length === 0
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  writeLocked === true ||
                  !onSaveDecisionPackageDraft ||
                  isSavingDecisionPackageDraft ||
                  decisionDocketKeys.length === 0
                    ? 0.7
                    : 1,
              }}
            >
              <FileText style={{ width: 12, height: 12, color: c.text }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: c.text }}>
                {isSavingDecisionPackageDraft ? 'Saving…' : 'Save draft'}
              </span>
            </button>
          ) : (
            <button
              onClick={() => {
                if (!onStartDecisionPackageAmendment) return;
                void onStartDecisionPackageAmendment();
              }}
              disabled={writeLocked || !onStartDecisionPackageAmendment || isStartingDecisionPackageAmendment === true}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '8px 12px',
                borderRadius: 6,
                backgroundColor: c.cardMuted,
                border: `1px solid ${c.border}`,
                cursor:
                  writeLocked || !onStartDecisionPackageAmendment || isStartingDecisionPackageAmendment
                    ? 'not-allowed'
                    : 'pointer',
                opacity: writeLocked || !onStartDecisionPackageAmendment || isStartingDecisionPackageAmendment ? 0.7 : 1,
              }}
            >
              <GitMerge style={{ width: 12, height: 12, color: c.text }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: c.text }}>
                {isStartingDecisionPackageAmendment ? 'Creating draft…' : 'Amend'}
              </span>
            </button>
          )}
          <button
            onClick={() => setIsActivateDialogOpen(true)}
            disabled={
              writeLocked === true ||
              isDecisionPackageActive === true ||
              !onActivateDecisionPackage ||
              isActivatingDecisionPackage === true ||
              decisionDocketKeys.length === 0
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '8px 14px',
              borderRadius: 6,
              backgroundColor: isDecisionPackageActive ? c.greenLight : c.green,
              border: isDecisionPackageActive ? `1px solid ${c.greenBorder}` : 'none',
              cursor:
                writeLocked === true ||
                isDecisionPackageActive ||
                !onActivateDecisionPackage ||
                isActivatingDecisionPackage ||
                decisionDocketKeys.length === 0
                  ? 'not-allowed'
                  : 'pointer',
              opacity:
                writeLocked === true ||
                isDecisionPackageActive ||
                !onActivateDecisionPackage ||
                isActivatingDecisionPackage ||
                decisionDocketKeys.length === 0
                  ? 0.7
                  : 1,
            }}
          >
            <CheckCircle2
              style={{
                width: 12,
                height: 12,
                color: isDecisionPackageActive ? c.green : '#fff',
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: isDecisionPackageActive ? c.green : '#fff',
              }}
            >
              {isDecisionPackageActive ? 'Active' : isActivatingDecisionPackage ? 'Activating…' : 'Approve & Activate'}
            </span>
          </button>
          <button
            onClick={onExportBoardMemo}
            disabled={!onExportBoardMemo || isExportingBoardMemo === true}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '8px 14px',
              borderRadius: 6,
              backgroundColor: c.accent,
              border: 'none',
              cursor: !onExportBoardMemo || isExportingBoardMemo ? 'not-allowed' : 'pointer',
              opacity: !onExportBoardMemo || isExportingBoardMemo ? 0.7 : 1,
            }}
          >
            <FileText style={{ width: 12, height: 12, color: '#fff' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>
              {isExportingBoardMemo ? 'Exporting…' : 'Export Board Memo'}
            </span>
          </button>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MAIN CONTENT */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <main style={{ maxWidth: 1480, margin: '0 auto', padding: '16px 20px' }}>
        {topSlot ? (
          <div data-testid="daily-brief-top-slot" style={{ marginBottom: 12 }}>
            {topSlot}
          </div>
        ) : null}

        {/* Active Filter Indicator */}
        {activeRiskTile && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              marginBottom: 12,
              backgroundColor: c.amberLight,
              border: `1px solid ${c.amberBorder}`,
              borderRadius: 6,
            }}
          >
            <Filter style={{ width: 12, height: 12, color: c.amber }} />
            <span style={{ fontSize: 13, color: c.text }}>
              Filtered by: <strong>{activeRiskTile.label}</strong>
            </span>
            <button
              onClick={() => setActiveRiskFilterId(null)}
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 8px',
                borderRadius: 4,
                backgroundColor: c.card,
                border: `1px solid ${c.border}`,
                fontSize: 12,
                color: c.textSecondary,
                cursor: 'pointer',
              }}
            >
              <X style={{ width: 10, height: 10 }} />
              Clear filter
            </button>
          </div>
        )}

        {/* Evidence / Data Gaps (no click-to-discover) */}
        {showEvidenceGapBanner ? (
          <div
            data-testid="evidence-gap-banner"
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px 14px',
              marginBottom: 12,
              backgroundColor: evidenceGapSeverity === 'critical' ? c.redLight : c.amberLight,
              border: `1px solid ${evidenceGapSeverity === 'critical' ? c.redBorder : c.amberBorder}`,
              borderRadius: 8,
              alignItems: 'flex-start',
            }}
          >
            <AlertTriangle
              style={{
                width: 14,
                height: 14,
                marginTop: 1,
                color: evidenceGapSeverity === 'critical' ? c.red : c.amber,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Evidence & data gaps</div>
              <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 2, lineHeight: 1.5 }}>
                {workSignalsStatus !== 'connected' ? (
                  <div>
                    <strong>Work signals:</strong>{' '}
                    {workSignalsStatus === 'stale' ? 'stale' : 'not connected'}
                    {workSignalsDetail ? ` — ${workSignalsDetail}` : null}
                    {writeLocked ? ' Refresh requires write access.' : ' Use “Refresh snapshot” before approving & activating.'}
                  </div>
                ) : null}

                {orgBaselineStatus !== 'connected' && hasCapacityGapAssumptions ? (
                  <div style={{ marginTop: workSignalsStatus !== 'connected' ? 4 : 0 }}>
                    <strong>Org baseline:</strong>{' '}
                    {orgBaselineDetail ? orgBaselineDetail : 'Not connected. Capacity gaps may be assumptions until the org graph is connected.'}
                  </div>
                ) : null}

                <div style={{ marginTop: 6, fontSize: 11, color: c.textMuted }}>
                  Evidence summary: {evidenceCounts.connected} connected • {evidenceCounts.stale} stale • {evidenceCounts.notConnected} not connected
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ROW 1: Executive takeaway + Decision Docket */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 340px',
            gap: 16,
            marginBottom: 16,
          }}
        >
          {/* Executive takeaway */}
          <div
            data-testid="executive-verdict"
            style={{
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              padding: '18px 22px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Target style={{ width: 14, height: 14, color: c.accent }} />
              <h1
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'Georgia, serif',
                  color: c.text,
                  margin: 0,
                }}
              >
                Executive takeaway
              </h1>
              <span style={{ fontSize: 12, color: c.textMuted }}>Daily Brief</span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginLeft: 'auto',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <RAGDot status="green" size={7} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: c.green }}>
                    {ragCounts.green}
                  </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <RAGDot status="amber" size={7} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: c.amber }}>
                    {ragCounts.amber}
                  </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <RAGDot status="red" size={7} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: c.red }}>
                    {ragCounts.red}
                  </span>
                </span>
              </div>
            </div>

            <div style={{ borderLeft: `3px solid ${c.accent}`, paddingLeft: 14 }}>
              <p
                style={{
                  fontSize: 15,
                  fontFamily: 'Georgia, serif',
                  color: c.text,
                  margin: '0 0 8px',
                  lineHeight: 1.5,
                }}
              >
                <span style={{ fontWeight: 700, color: c.red }}>What will miss:</span>{' '}
                {data.executiveVerdict.whatWillMiss}
              </p>
              <p
                style={{
                  fontSize: 15,
                  fontFamily: 'Georgia, serif',
                  color: c.text,
                  margin: '0 0 8px',
                  lineHeight: 1.5,
                }}
              >
                <span style={{ fontWeight: 700 }}>Binding constraint:</span>{' '}
                {data.executiveVerdict.bindingConstraint}
              </p>
              <p
                style={{
                  fontSize: 15,
                  fontFamily: 'Georgia, serif',
                  color: c.text,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                <span style={{ fontWeight: 700, color: c.accent }}>
                  Recommendation:
                </span>{' '}
                {data.executiveVerdict.decisionThatChangesOutcome}
              </p>
            </div>
          </div>

          {/* Decision Docket */}
          <div data-testid="decision-docket">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 10,
              }}
            >
              <GitMerge style={{ width: 13, height: 13, color: c.accent }} />
              <h2
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: c.text,
                  margin: 0,
                }}
              >
                Decision Docket
              </h2>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 6px',
                  backgroundColor: c.redLight,
                  color: c.red,
                  borderRadius: 10,
                  marginLeft: 'auto',
                }}
              >
                {data.decisionDocket.length} pending
              </span>
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {data.decisionDocket.map((decision) => (
                <DecisionCard
                  key={decision.id}
                  decision={decision}
                  priorityDrivers={
                    decision.linkedInitiativeKey
                      ? data.bets.find((b) => b.key === decision.linkedInitiativeKey)?.priorityDrivers
                      : undefined
                  }
                  isActive={selectedDecisionId === decision.id}
                  onClick={() => {
                    setSelectedDecisionId(decision.id);
                    if (decision.linkedInitiativeKey) {
                      setSelectedBetKey(decision.linkedInitiativeKey);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ROW 1.5: Measurement */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {activationBaseline && activationDeltaRows.length ? (
          <div
            data-testid="activation-measurement"
            style={{
              marginTop: 14,
              marginBottom: 16,
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <TrendingUp style={{ width: 13, height: 13, color: c.accent }} />
                <h2 style={{ fontSize: 13, fontWeight: 700, color: c.text, margin: 0 }}>Measurement</h2>
                <span style={{ fontSize: 12, color: c.textMuted }}>
                  Since last activation • Baseline {formatAsOfDate(activationBaseline.baselineAsOf)} • v{activationBaseline.version}
                  {(() => {
                    const missing = activationDeltaRows.filter((r) => {
                      const text = String(r.decision?.successCriteria ?? '').trim();
                      const t = r.decision?.targets ?? null;
                      const hasStructured =
                        t != null &&
                        (Boolean(t.targetRag) ||
                          t.maxSlipWeeks != null ||
                          t.maxGapPoints != null ||
                          t.maxStalledCount != null);
                      return !text && !hasStructured;
                    }).length;
                    return missing ? ` • Targets missing for ${missing}` : '';
                  })()}
                </span>
              </div>
              {canReviewDecisionPackage ? (
                <button
                  type="button"
                  onClick={() => setIsReviewDialogOpen(true)}
                  disabled={isReviewingDecisionPackage === true}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '7px 10px',
                    borderRadius: 8,
                    backgroundColor: c.cardMuted,
                    border: `1px solid ${c.border}`,
                    cursor:
                      isReviewingDecisionPackage === true
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: isReviewingDecisionPackage === true ? 0.7 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <CheckCircle2 style={{ width: 12, height: 12, color: c.textMuted }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: c.text }}>
                    {isReviewingDecisionPackage ? 'Marking…' : 'Mark reviewed'}
                  </span>
                </button>
              ) : null}
            </div>

            <div style={{ border: `1px solid ${c.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2.4fr 1fr 1fr 1fr 1.1fr',
                  gap: 0,
                  backgroundColor: c.cardMuted,
                  borderBottom: `1px solid ${c.border}`,
                  fontSize: 11,
                  fontWeight: 700,
                  color: c.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                <div style={{ padding: '10px 12px' }}>Initiative</div>
                <div style={{ padding: '10px 12px' }}>RAG</div>
                <div style={{ padding: '10px 12px' }}>Slip (w)</div>
                <div style={{ padding: '10px 12px' }}>Gap (pts)</div>
                <div style={{ padding: '10px 12px' }}>Stalled</div>
              </div>

              {activationDeltaRows.map((row, idx) => {
                const betName = data.bets.find((b) => b.key === row.initiativeKey)?.name ?? row.initiativeKey;
                const decision = row.decision ?? null;
                const ownerUserId = decision?.ownerUserId ?? null;
                const ownerLabel = formatExecutiveOwnerLabel(ownerUserId, ownerCandidateLabelById);
                const cadenceRaw = String(decision?.reviewCadence ?? '').trim().toLowerCase();
                const cadenceLabel =
                  cadenceRaw === 'weekly'
                    ? 'Weekly'
                    : cadenceRaw === 'biweekly'
                      ? 'Biweekly'
                      : cadenceRaw === 'monthly'
                        ? 'Monthly'
                        : 'Not set';
                const targetsText = String(decision?.successCriteria ?? '').trim() || null;
                const structuredTargets = formatStructuredTargetsFromDto(decision?.targets);
                const lastReviewedIso = decision?.lastReviewedAt ?? null;
                const lastReviewedLabel = lastReviewedIso ? formatShortDate(lastReviewedIso) : 'Not logged';
                const nextReviewDueIso = activationBaseline?.activatedAt
                  ? computeNextReviewDueIso({
                      referenceAt: lastReviewedIso ?? activationBaseline.activatedAt,
                      cadence: decision?.reviewCadence ?? null,
                    })
                  : null;
                const nextReviewDueLabel = nextReviewDueIso ? formatShortDate(nextReviewDueIso) : cadenceRaw ? '—' : 'Not set';
                const isReviewOverdue = (() => {
                  if (!nextReviewDueIso) return false;
                  const ms = new Date(nextReviewDueIso).getTime();
                  if (!Number.isFinite(ms)) return false;
                  return Date.now() > ms;
                })();
                const overdueLabel = isReviewOverdue ? (lastReviewedIso ? 'Overdue' : 'Overdue (no review logged)') : null;

                const targetsStatus = (() => {
                  const t = decision?.targets ?? null;
                  const hasAny =
                    t != null &&
                    (Boolean(t.targetRag) ||
                      t.maxSlipWeeks != null ||
                      t.maxGapPoints != null ||
                      t.maxStalledCount != null);
                  if (!hasAny) return { label: 'Not set', color: c.textMuted };

                  const checks: Array<boolean | null> = [];
                  const ragRank = (rag: string | null) => (rag === 'green' ? 3 : rag === 'amber' ? 2 : rag === 'red' ? 1 : 0);

                  if (t?.targetRag) {
                    const current = row.current.rag;
                    checks.push(current == null ? null : ragRank(current) >= ragRank(t.targetRag));
                  }
                  if (t?.maxSlipWeeks != null) {
                    checks.push(row.current.slipWeeks == null ? null : row.current.slipWeeks <= t.maxSlipWeeks);
                  }
                  if (t?.maxGapPoints != null) {
                    checks.push(row.current.gapPoints == null ? null : row.current.gapPoints <= t.maxGapPoints);
                  }
                  if (t?.maxStalledCount != null) {
                    checks.push(row.current.stalledCount == null ? null : row.current.stalledCount <= t.maxStalledCount);
                  }

                  if (checks.some((c) => c === false)) return { label: 'Off-track', color: c.red };
                  if (checks.some((c) => c == null)) return { label: 'Unknown', color: c.amber };
                  return { label: 'Met', color: c.green };
                })();

                const ragLabel = (rag: string | null) => (rag ? rag.toUpperCase() : '—');
                const fmtNum = (n: number | null) => (n == null ? '—' : String(n));
                const fmtDelta = (n: number | null) => {
                  if (n == null) return '—';
                  if (n === 0) return '0';
                  return `${n > 0 ? '+' : ''}${n}`;
                };
                const deltaColor = (n: number | null) => {
                  if (n == null) return c.textMuted;
                  if (n < 0) return c.green;
                  if (n > 0) return c.red;
                  return c.textSecondary;
                };

                return (
                  <div
                    key={row.initiativeKey}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2.4fr 1fr 1fr 1fr 1.1fr',
                      gap: 0,
                      backgroundColor: idx % 2 ? c.card : c.cardMuted,
                      borderBottom: idx === activationDeltaRows.length - 1 ? 'none' : `1px solid ${c.border}`,
                      fontSize: 12,
                      color: c.text,
                    }}
                  >
                    <div style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 700 }}>{betName}</div>
                      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                        Owner: {ownerLabel} • Cadence: {cadenceLabel} • Last review: {lastReviewedLabel} • Next due: {nextReviewDueLabel}
                        {overdueLabel ? (
                          <span style={{ color: c.red, fontWeight: 700 }}> • {overdueLabel}</span>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 11, color: targetsStatus.color, marginTop: 2, lineHeight: 1.4, fontWeight: 700 }}>
                        Targets status: {targetsStatus.label}
                      </div>
                      {structuredTargets ? (
                        <div
                          title={structuredTargets}
                          style={{
                            fontSize: 11,
                            color: c.textMuted,
                            marginTop: 2,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          Targets (metrics): {structuredTargets}
                        </div>
                      ) : null}
                      {targetsText ? (
                        <div
                          title={targetsText}
                          style={{
                            fontSize: 11,
                            color: c.textMuted,
                            marginTop: 2,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          Targets (text): {targetsText}
                        </div>
                      ) : null}
                      {!structuredTargets && !targetsText ? (
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                          Targets: Not set
                        </div>
                      ) : null}
                    </div>
                    <div style={{ padding: '10px 12px', color: c.textSecondary }}>
                      {ragLabel(row.baseline.rag)} → {ragLabel(row.current.rag)}
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      {fmtNum(row.baseline.slipWeeks)} → {fmtNum(row.current.slipWeeks)}{' '}
                      <span style={{ color: deltaColor(row.delta.slipWeeks), fontWeight: 700 }}>
                        (Δ {fmtDelta(row.delta.slipWeeks)})
                      </span>
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      {fmtNum(row.baseline.gapPoints)} → {fmtNum(row.current.gapPoints)}{' '}
                      <span style={{ color: deltaColor(row.delta.gapPoints), fontWeight: 700 }}>
                        (Δ {fmtDelta(row.delta.gapPoints)})
                      </span>
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      {fmtNum(row.baseline.stalledCount)} → {fmtNum(row.current.stalledCount)}{' '}
                      <span style={{ color: deltaColor(row.delta.stalledCount), fontWeight: 700 }}>
                        (Δ {fmtDelta(row.delta.stalledCount)})
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 10, lineHeight: 1.4 }}>
              Δ = current − baseline (negative is improvement). Slip definition: max(0, ceil(weeksNeeded − weeksRemaining)).
              {' '}
              Next review due is derived from last review log + cadence; when no review log exists, activation timestamp is used as the reference. Overdue means next due date has passed with no newer review logged. Structured targets evaluate current metrics; “Unknown” means a required metric is missing.
            </div>
          </div>
        ) : null}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ROW 2: Risk Radar */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div data-testid="risk-radar" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 10,
            }}
          >
            <AlertTriangle style={{ width: 13, height: 13, color: c.accent }} />
            <h2 style={{ fontSize: 13, fontWeight: 700, color: c.text, margin: 0 }}>Risk Radar</h2>
            <span style={{ fontSize: 12, color: c.textMuted }}>
              Today&apos;s highest-severity items • {deltaBasisLabel} • Click to filter
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {data.riskRadar.map((tile) => {
              const Icon = riskRadarIcons[tile.id] ?? AlertTriangle;
              return (
                <RiskRadarTile
                  key={tile.id}
                  tile={tile}
                  icon={Icon}
                  isActive={activeRiskFilterId === tile.id}
                  onClick={() =>
                    setActiveRiskFilterId(activeRiskFilterId === tile.id ? null : tile.id)
                  }
                />
              );
            })}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ROW 3: Strategic Bets */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div data-testid="strategic-bets" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 10,
            }}
          >
            <Layers style={{ width: 13, height: 13, color: c.accent }} />
            <h2 style={{ fontSize: 13, fontWeight: 700, color: c.text, margin: 0 }}>
              Strategic Bets
            </h2>
            <span style={{ fontSize: 12, color: c.textMuted }}>
              {data.filters.timeWindowLabel} Portfolio • Click for detail
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(visibleBets.length, 5)}, 1fr)`,
              gap: 12,
            }}
          >
            {visibleBets.map((bet) => (
              <BetCard
                key={bet.key}
                bet={bet}
                isSelected={selectedBetKey === bet.key}
                onClick={() => setSelectedBetKey(bet.key)}
              />
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ROW 4: Deep Dive */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 300px',
            gap: 16,
          }}
        >
          {/* Constraint → Proof → Options */}
          <div
            data-testid="deep-dive"
            style={{
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              padding: 18,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <RAGDot status={selectedBet.rag} size={10} />
                  <h3
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      fontFamily: 'Georgia, serif',
                      color: c.text,
                      margin: 0,
                    }}
                  >
                    {selectedBet.name}
                  </h3>
                  <ConfidenceBadge
                    level={selectedBet.confidence}
                    reason={selectedBet.confidenceReason}
                  />
                </div>
                <div style={{ fontSize: 12, color: c.textMuted }}>
                  Accountability: {formatExecutiveAccountabilityLabel(selectedBet.accountability)}
                  {portfolioRank ? ` • Portfolio rank #${portfolioRank}/${data.bets.length}` : ''}
                </div>
              </div>
              <div
                style={{
                  padding: '6px 12px',
                  backgroundColor: c.cardMuted,
                  borderRadius: 6,
                  textAlign: 'right',
                }}
              >
                <div style={{ fontSize: 11, color: c.textMuted }}>Timeline</div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    fontFamily: 'Georgia, serif',
                    color:
                      selectedBet.weeksNeeded > selectedBet.weeksLeft ? c.red : c.text,
                  }}
                >
                  {selectedBet.weeksNeeded}w / {selectedBet.weeksLeft}w
                </div>
              </div>
            </div>

            {Array.isArray(selectedBet.priorityDrivers) && selectedBet.priorityDrivers.length ? (
              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 10,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: c.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Priority drivers
                  </div>
                  <div style={{ fontSize: 11, color: c.textMuted }}>
                    Ordered by status → value → slip → gap → stalled
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedBet.priorityDrivers.map((d) => (
                    <PriorityDriverChip key={d.id} driver={d} />
                  ))}
                </div>
              </div>
            ) : null}

            {decisionDocketKeySet.has(selectedBet.key)
              ? (() => {
                  const activeDecision = decisionByKey.get(selectedBet.key) ?? null;
                  const draftDetails = draftDecisionDetails[selectedBet.key] ?? emptyDraftDecisionDetails();

                  const ownerValue = isDecisionPackageActive
                    ? String(activeDecision?.ownerUserId ?? '').trim()
                    : draftDetails.ownerUserId;
                  const dueDateValue = isDecisionPackageActive
                    ? (() => {
                        const iso = activeDecision?.dueAt ?? null;
                        if (!iso) return '';
                        const date = new Date(iso);
                        if (Number.isNaN(date.getTime())) return '';
                        return date.toISOString().slice(0, 10);
                      })()
                    : draftDetails.dueDate;
                  const rationaleValue = isDecisionPackageActive
                    ? String(activeDecision?.rationale ?? '').trim()
                    : draftDetails.rationale;
                  const reviewCadenceValue = isDecisionPackageActive
                    ? String(activeDecision?.reviewCadence ?? '').trim()
                    : draftDetails.reviewCadence;
                  const successCriteriaValue = isDecisionPackageActive
                    ? String(activeDecision?.successCriteria ?? '').trim()
                    : draftDetails.successCriteria;
                  const targetRagValue = isDecisionPackageActive
                    ? String(activeDecision?.targets?.targetRag ?? '').trim()
                    : String(draftDetails.targets?.targetRag ?? '').trim();
                  const maxSlipWeeksValue = isDecisionPackageActive
                    ? activeDecision?.targets?.maxSlipWeeks == null
                      ? ''
                      : String(activeDecision.targets.maxSlipWeeks)
                    : String(draftDetails.targets?.maxSlipWeeks ?? '');
                  const maxGapPointsValue = isDecisionPackageActive
                    ? activeDecision?.targets?.maxGapPoints == null
                      ? ''
                      : String(activeDecision.targets.maxGapPoints)
                    : String(draftDetails.targets?.maxGapPoints ?? '');
                  const maxStalledCountValue = isDecisionPackageActive
                    ? activeDecision?.targets?.maxStalledCount == null
                      ? ''
                      : String(activeDecision.targets.maxStalledCount)
                    : String(draftDetails.targets?.maxStalledCount ?? '');

                  const isEditable =
                    canEditDecisionPackage === true &&
                    isActivatingDecisionPackage !== true &&
                    isSavingDecisionPackageDraft !== true;

                  return (
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 8,
                        marginBottom: 14,
                        backgroundColor: c.cardMuted,
                        border: `1px solid ${c.border}`,
                      }}
                      data-testid="decision-package-fields"
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginBottom: 10,
                        }}
                      >
                        <Target style={{ width: 12, height: 12, color: c.accent }} />
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: c.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Accountability & Targets
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                          {audience !== 'operator' ? (
                            <button
                              type="button"
                              onClick={() => setIsAccountabilityPanelOpen((prev) => !prev)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '6px 10px',
                                borderRadius: 8,
                                border: `1px solid ${c.border}`,
                                backgroundColor: c.card,
                                color: c.textSecondary,
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                              data-testid="accountability-toggle"
                            >
                              {isAccountabilityPanelOpen ? 'Hide' : 'Show'}
                            </button>
                          ) : null}
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: isDecisionPackageActive ? c.green : c.textSecondary,
                            }}
                          >
                            {isDecisionPackageActive ? 'Locked (active)' : 'Draft'}
                          </div>
                        </div>
                      </div>

                      {isAccountabilityPanelOpen ? (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                            {audience === 'operator' ? 'Owner' : 'Sponsor'}
                          </div>
                          {audience === 'operator'
                            ? (() => {
                                const directoryLoaded = Array.isArray(ownerCandidates);
                                const hasCandidates = directoryLoaded && ownerCandidateById.size > 0;
                                const canPickOwner = isEditable === true && hasCandidates;

                                return (
                                  <>
                                    <select
                                      value={ownerValue}
                                      disabled={canPickOwner !== true}
                                      onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setDraftDecisionDetails((prev) => {
                                          const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                                          return { ...prev, [selectedBet.key]: { ...existing, ownerUserId: nextValue } };
                                        });
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 10px',
                                        borderRadius: 8,
                                        border: `1px solid ${c.border}`,
                                        backgroundColor: canPickOwner ? c.card : c.cardMuted,
                                        color: c.text,
                                        fontSize: 12,
                                      }}
                                    >
                                      <option value="">Unassigned</option>
                                      {ownerValue && !ownerCandidateById.has(ownerValue) ? (
                                        <option value={ownerValue}>(Unrecognized) {ownerValue}</option>
                                      ) : null}
                                      {(ownerCandidates ?? []).map((m) => (
                                        <option key={m.userId} value={m.userId}>
                                          {m.label}
                                          {m.email ? ` (${m.email})` : ''}
                                        </option>
                                      ))}
                                    </select>
                                    {directoryLoaded !== true ? (
                                      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
                                        Owner directory unavailable. Choose Unassigned or ask an admin to enable directory access.
                                      </div>
                                    ) : ownerCandidates.length === 0 ? (
                                      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
                                        No active members found for this tenant.
                                      </div>
                                    ) : null}
                                  </>
                                );
                              })()
                            : (
                                <div
                                  data-testid="decision-package-sponsor"
                                  style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    border: `1px solid ${c.border}`,
                                    backgroundColor: c.cardMuted,
                                    color: c.text,
                                    fontSize: 12,
                                  }}
                                >
                                  {(() => {
                                    const raw = String(selectedBet.accountability ?? '').trim();
                                    if (raw && raw !== 'Unassigned') {
                                      return formatExecutiveAccountabilityLabel(raw);
                                    }
                                    return 'Unassigned';
                                  })()}
                                </div>
                              )}
                          {audience === 'operator' &&
                          String(selectedBet.accountability ?? '').trim() &&
                          String(selectedBet.accountability ?? '').trim() !== 'Unassigned' ? (
                            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                              Initiative accountability: {formatExecutiveAccountabilityLabel(selectedBet.accountability)} (not auto-assigned)
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                            Due date
                          </div>
                          <input
                            type="date"
                            value={dueDateValue}
                            disabled={isEditable !== true}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setDraftDecisionDetails((prev) => {
                                const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                                return { ...prev, [selectedBet.key]: { ...existing, dueDate: nextValue } };
                              });
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              borderRadius: 8,
                              border: `1px solid ${c.border}`,
                              backgroundColor: isEditable ? c.card : c.cardMuted,
                              color: c.text,
                              fontSize: 12,
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                          Review cadence
                        </div>
                        <select
                          value={reviewCadenceValue}
                          disabled={isEditable !== true}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setDraftDecisionDetails((prev) => {
                              const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                              return { ...prev, [selectedBet.key]: { ...existing, reviewCadence: nextValue } };
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: `1px solid ${c.border}`,
                            backgroundColor: isEditable ? c.card : c.cardMuted,
                            color: c.text,
                            fontSize: 12,
                          }}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Biweekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
                          Used for executive review cadence; shown in Measurement after activation.
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                          Success criteria (targets)
                        </div>
                        <textarea
                          value={successCriteriaValue}
                          disabled={isEditable !== true}
                          placeholder="e.g., RAG=GREEN; slipWeeks<=0; gapPoints<=0; stalledCount<=1"
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setDraftDecisionDetails((prev) => {
                              const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                              return { ...prev, [selectedBet.key]: { ...existing, successCriteria: nextValue } };
                            });
                          }}
                          style={{
                            width: '100%',
                            minHeight: 54,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: `1px solid ${c.border}`,
                            backgroundColor: isEditable ? c.card : c.cardMuted,
                            color: c.text,
                            fontSize: 12,
                            resize: 'vertical',
                          }}
                        />
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
                          Stored in the decision package draft; used as the target definition in Measurement.
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                          Structured targets (optional)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                              Target RAG
                            </div>
                            <select
                              value={targetRagValue}
                              disabled={isEditable !== true}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setDraftDecisionDetails((prev) => {
                                  const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                                  return {
                                    ...prev,
                                    [selectedBet.key]: {
                                      ...existing,
                                      targets: { ...existing.targets, targetRag: nextValue },
                                    },
                                  };
                                });
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: 8,
                                border: `1px solid ${c.border}`,
                                backgroundColor: isEditable ? c.card : c.cardMuted,
                                color: c.text,
                                fontSize: 12,
                              }}
                            >
                              <option value="">Not set</option>
                              <option value="green">Green</option>
                              <option value="amber">Amber</option>
                              <option value="red">Red</option>
                            </select>
                          </div>

                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                              Max slip (weeks)
                            </div>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={maxSlipWeeksValue}
                              disabled={isEditable !== true}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setDraftDecisionDetails((prev) => {
                                  const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                                  return {
                                    ...prev,
                                    [selectedBet.key]: {
                                      ...existing,
                                      targets: { ...existing.targets, maxSlipWeeks: nextValue },
                                    },
                                  };
                                });
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: 8,
                                border: `1px solid ${c.border}`,
                                backgroundColor: isEditable ? c.card : c.cardMuted,
                                color: c.text,
                                fontSize: 12,
                              }}
                            />
                          </div>

                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                              Max gap (points)
                            </div>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={maxGapPointsValue}
                              disabled={isEditable !== true}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setDraftDecisionDetails((prev) => {
                                  const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                                  return {
                                    ...prev,
                                    [selectedBet.key]: {
                                      ...existing,
                                      targets: { ...existing.targets, maxGapPoints: nextValue },
                                    },
                                  };
                                });
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: 8,
                                border: `1px solid ${c.border}`,
                                backgroundColor: isEditable ? c.card : c.cardMuted,
                                color: c.text,
                                fontSize: 12,
                              }}
                            />
                          </div>

                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                              Max stalled (items)
                            </div>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={maxStalledCountValue}
                              disabled={isEditable !== true}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setDraftDecisionDetails((prev) => {
                                  const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                                  return {
                                    ...prev,
                                    [selectedBet.key]: {
                                      ...existing,
                                      targets: { ...existing.targets, maxStalledCount: nextValue },
                                    },
                                  };
                                });
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                borderRadius: 8,
                                border: `1px solid ${c.border}`,
                                backgroundColor: isEditable ? c.card : c.cardMuted,
                                color: c.text,
                                fontSize: 12,
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
                          Stored in the decision package draft; evaluated deterministically in Measurement after activation.
                        </div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>
                          Rationale
                        </div>
                        <textarea
                          value={rationaleValue}
                          disabled={isEditable !== true}
                          placeholder="Rationale (optional)"
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setDraftDecisionDetails((prev) => {
                              const existing = prev[selectedBet.key] ?? emptyDraftDecisionDetails();
                              return { ...prev, [selectedBet.key]: { ...existing, rationale: nextValue } };
                            });
                          }}
                          style={{
                            width: '100%',
                            minHeight: 66,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: `1px solid ${c.border}`,
                            backgroundColor: isEditable ? c.card : c.cardMuted,
                            color: c.text,
                            fontSize: 12,
                            resize: 'vertical',
                          }}
                        />
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
                          Stored in the decision package draft. No changes are applied until you approve &amp; activate.
                        </div>
                      </div>
                        </>
                      ) : null}
                    </div>
                  );
                })()
              : null}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 18,
              }}
            >
              {/* Constraint Analysis */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: c.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 8,
                  }}
                >
                  Constraint Analysis
                </div>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 12,
                    backgroundColor:
                      selectedBet.rag === 'green'
                        ? c.greenLight
                        : selectedBet.rag === 'amber'
                          ? c.amberLight
                          : c.redLight,
                    border: `1px solid ${
                      selectedBet.rag === 'green'
                        ? c.greenBorder
                        : selectedBet.rag === 'amber'
                          ? c.amberBorder
                          : c.redBorder
                    }`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: c.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 3,
                    }}
                  >
                    Binding Constraint
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: c.text,
                      lineHeight: 1.5,
                    }}
                  >
                    {selectedBet.constraint}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: c.textMuted,
                    marginBottom: 6,
                  }}
                >
                  Proof Anchors ({selectedBet.proofs.length}) • {proofBasisSummary}
                </div>
                {selectedBet.proofs.map((proof, i) => (
                  <ProofAnchor key={i} proof={proof} />
                ))}
              </div>

              {/* Decision Options */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: c.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 8,
                  }}
                >
                  Decision Options
                </div>
                {selectedBet.options.length > 0 ? (
                  (() => {
                    const selectedOptionIndex = isDecisionPackageActive
                      ? activeDecisionSelections.get(selectedBet.key) ?? null
                      : draftDecisionSelections[selectedBet.key] ?? null;
                    const canSelect =
                      canEditDecisionPackage === true &&
                      decisionDocketKeySet.has(selectedBet.key) &&
                      isActivatingDecisionPackage !== true &&
                      isSavingDecisionPackageDraft !== true;

                    return selectedBet.options.map((opt, i) => {
                      const Icon = optionKindIcons[opt.kind] ?? Zap;
                      return (
                        <DecisionOptionBtn
                          key={i}
                          option={opt}
                          icon={Icon}
                          isPrimary={i === 0}
                          isSelected={selectedOptionIndex === i}
                          isDisabled={canSelect !== true}
                          onSelect={
                            canSelect
                              ? () =>
                                  setDraftDecisionSelections((prev) => ({
                                    ...prev,
                                    [selectedBet.key]: i,
                                  }))
                              : undefined
                          }
                        />
                      );
                    });
                  })()
                ) : (
                  <div
                    style={{
                      padding: 20,
                      textAlign: 'center',
                      backgroundColor: c.greenLight,
                      border: `1px solid ${c.greenBorder}`,
                      borderRadius: 8,
                    }}
                  >
                    <CheckCircle2
                      style={{
                        width: 24,
                        height: 24,
                        color: c.green,
                        margin: '0 auto 6px',
                      }}
                    />
                    <div style={{ fontSize: 14, fontWeight: 600, color: c.green }}>On Track</div>
                    <div style={{ fontSize: 13, color: c.textSecondary, marginTop: 4 }}>
                      No decision required.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Capacity + Evidence */}
          <div>
            {/* Capacity vs Strategy Demand */}
            <div
              style={{
                backgroundColor: c.card,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  marginBottom: 10,
                }}
              >
                <Building2 style={{ width: 11, height: 11, color: c.accent }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: c.text }}>
                  Capacity vs Strategy Demand
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {data.capacityGaps.length ? (
                  data.capacityGaps.map((gap) => {
                    const shortPoints = Math.abs(gap.deltaPoints);
                    const shortPercent = gap.deltaPercent != null ? Math.abs(gap.deltaPercent) : null;
                    const barHeight = Math.min((shortPercent ?? 0) + 50, 95);

                    return (
                      <div key={gap.initiativeKey} style={{ flex: 1, textAlign: 'center' }}>
                        <div
                          style={{
                            height: 40,
                            backgroundColor: c.cardMuted,
                            borderRadius: 4,
                            position: 'relative',
                            overflow: 'hidden',
                            marginBottom: 4,
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: '15%',
                              right: '15%',
                              height: `${barHeight}%`,
                              backgroundColor: c.redLight,
                              borderTop: `2px solid ${c.red}`,
                            }}
                          />
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: c.textSecondary }}>
                          {gap.name}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: c.red }}>
                          Short {shortPoints} pts{shortPercent != null ? ` (${shortPercent}%)` : ''}
                        </div>
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                          Demand {gap.demandPoints} • Supply {gap.supplyPointsByDeadline}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: 10,
                      backgroundColor: c.cardMuted,
                      border: `1px solid ${c.border}`,
                      borderRadius: 6,
                      width: '100%',
                      fontSize: 12,
                      color: c.textMuted,
                      lineHeight: 1.5,
                    }}
                  >
                    {orgBaselineStatus === 'connected'
                      ? 'No capacity gaps detected (supply meets demand by deadline).'
                      : 'Capacity gaps require an org baseline and owner mapping (connect org graph / map initiative owners).'}
                  </div>
                )}
              </div>
              <a
                href={links?.orgCanvas ?? undefined}
                aria-disabled={!links?.orgCanvas}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  borderRadius: 4,
                  backgroundColor: 'transparent',
                  border: `1px solid ${c.border}`,
                  fontSize: 11,
                  color: c.accent,
                  fontWeight: 500,
                  cursor: links?.orgCanvas ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  opacity: links?.orgCanvas ? 1 : 0.6,
                  pointerEvents: links?.orgCanvas ? 'auto' : 'none',
                  textDecoration: 'none',
                }}
                data-testid="daily-brief-open-org-canvas"
                title={links?.orgCanvas ? 'Open Org Canvas' : 'Org Canvas link unavailable'}
              >
                Open Org Canvas <ExternalLink style={{ width: 10, height: 10 }} />
              </a>
            </div>

            {/* Evidence Coverage */}
            <div
              data-testid="evidence-coverage"
              style={{
                backgroundColor: c.card,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: 14,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  marginBottom: 10,
                }}
              >
                <Activity style={{ width: 11, height: 11, color: c.accent }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: c.text }}>
                  Evidence Coverage
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.evidenceCoverage.map((item) => {
                  const statusConfig = evidenceStatusColors[item.status] ?? evidenceStatusColors.not_connected;
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '5px 8px',
                        borderRadius: 4,
                        backgroundColor: statusConfig.bg,
                        border: `1px solid ${statusConfig.border}`,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: c.text }}>
                          {item.label}
                        </div>
                        {item.detail ? (
                          <div
                            style={{
                              fontSize: 11,
                              color: c.textMuted,
                              marginTop: 2,
                              lineHeight: 1.4,
                            }}
                          >
                            {item.detail}
                          </div>
                        ) : null}
                      </div>
                      <span style={{ fontSize: 11, color: statusConfig.color }}>
                        {statusConfig.icon}{' '}
                        {item.sourceLabel ?? (item.status === 'not_connected' ? 'Not connected' : '—')}
                      </span>
                    </div>
                  );
                })}
              </div>
              <a
                href={links?.evidenceCoverage ?? undefined}
                aria-disabled={!links?.evidenceCoverage}
                style={{
                  marginTop: 8,
                  width: '100%',
                  padding: '7px 10px',
                  borderRadius: 4,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  fontSize: 11,
                  color: c.textSecondary,
                  cursor: links?.evidenceCoverage ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  opacity: links?.evidenceCoverage ? 1 : 0.6,
                  pointerEvents: links?.evidenceCoverage ? 'auto' : 'none',
                  textDecoration: 'none',
                }}
                data-testid="daily-brief-improve-evidence"
                title={links?.evidenceCoverage ? 'Manage evidence sources' : 'Evidence coverage link unavailable'}
              >
                Improve evidence coverage <ChevronRight style={{ width: 10, height: 10 }} />
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* PROVENANCE + DEFINITIONS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {isProvenanceDialogOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="daily-brief-provenance-dialog"
          onClick={() => setIsProvenanceDialogOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 210,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(780px, 100%)',
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: 18,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: c.text }}>Provenance & Definitions</div>
                <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4, lineHeight: 1.4 }}>
                  Evidence-linked where available. Assumptions and data gaps are shown. No changes are applied without your approval.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsProvenanceDialogOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 10px',
                  borderRadius: 8,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: 12, height: 12, color: c.textMuted }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: c.text }}>Close</span>
              </button>
            </div>

            <div style={{ marginTop: 14, border: `1px solid ${c.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 14px', backgroundColor: c.cardMuted, borderBottom: `1px solid ${c.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: c.text }}>Evidence Basis</div>
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '160px 1fr',
                    gap: 8,
                    fontSize: 12,
                    color: c.textSecondary,
                    lineHeight: 1.4,
                  }}
                >
                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Evidence package</div>
                  <div style={{ color: c.text }}>Approved Daily Brief run</div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Strategy</div>
                  <div style={{ color: c.text }}>
                    {formatExecutiveStrategyScopeLabel(meta?.run?.strategyId, 'Current strategy')}
                  </div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Prepared at</div>
                  <div style={{ color: c.text }}>{String(meta?.run?.computedAt ?? data.asOf)}</div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Source</div>
                  <div style={{ color: c.text }}>{sourceKindLabel}</div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Evidence trail</div>
                  <div style={{ color: c.text }}>Retained in Helix for audit replay</div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Org baseline</div>
                  <div style={{ color: c.text }}>
                    {meta?.run?.orgBaseline ? String(meta.run.orgBaseline.updatedAt) : '—'}
                  </div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Rule set</div>
                  <div style={{ color: c.text }}>Approved Daily Brief rules</div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Stall threshold</div>
                  <div style={{ color: c.text }}>
                    {meta?.run?.config?.stalledDaysThreshold != null ? `${meta.run.config.stalledDaysThreshold} days` : '—'}
                  </div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Throughput lookback</div>
                  <div style={{ color: c.text }}>
                    {meta?.run?.config?.lookbackDaysForThroughput != null
                      ? `${meta.run.config.lookbackDaysForThroughput} days`
                      : '—'}
                  </div>

                  <div style={{ color: c.textMuted, fontWeight: 700 }}>Prior comparison</div>
                  <div style={{ color: c.text }}>
                    {meta?.previousRun
                      ? `As of ${formatAsOfDate(meta.previousRun.computedAt)} (${meta.isComparableToPreviousRun ? 'comparable' : 'not comparable'})`
                      : '—'}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, border: `1px solid ${c.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 14px', backgroundColor: c.cardMuted, borderBottom: `1px solid ${c.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: c.text }}>Decision-Grade Definitions</div>
              </div>
              <div style={{ padding: '12px 14px', fontSize: 12, color: c.textSecondary, lineHeight: 1.5 }}>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
                  <li>
                    <strong>Δ vs previous run</strong>: differences are computed only when the initiative set is comparable. If not comparable, deltas are shown as “—”.
                  </li>
                  <li>
                    <strong>Priority ordering</strong>: bets (and decision docket) are sorted by status (RED &gt; AMBER &gt; GREEN) → strategic value (HIGH &gt; MED &gt; LOW) → schedule slip (weeks) → gap points → stalled count → name. If gap points are unavailable, Helix treats them as 0 for ordering and flags that as an assumption.
                  </li>
                  <li>
                    <strong>Demand points</strong>: heuristic effort points (not hours). Used to estimate the work remaining for an initiative.
                  </li>
                  <li>
                    <strong>Supply points</strong>: capacity points/week × weeks remaining to deadline (only when org capacity is available).
                  </li>
                  <li>
                    <strong>Schedule slip</strong>: max(0, ceil(weeksNeeded − weeksRemaining)). Displayed as “+Xw” when positive.
                  </li>
                  <li>
                    <strong>Δ points / % (supply − demand)</strong>: negative indicates a shortfall; positive indicates buffer. Helix surfaces shortfalls as “Short X pts (Y%).”
                  </li>
                  <li>
                    <strong>Gap points</strong>: max(0, demand − supply by deadline). Used for option sizing when capacity is available.
                  </li>
                  <li>
                    <strong>Stalled count</strong>: open work items with no update beyond the configured stall threshold
                    {meta?.run?.config?.stalledDaysThreshold != null
                      ? ` (${meta.run.config.stalledDaysThreshold} days in this run)`
                      : ' (unknown for this run)'}
                    .
                  </li>
                  <li>
                    <strong>Throughput lookback</strong>: throughput metrics and trends are computed over the configured lookback window
                    {meta?.run?.config?.lookbackDaysForThroughput != null
                      ? ` (${meta.run.config.lookbackDaysForThroughput} days in this run)`
                      : ' (unknown for this run)'}
                    .
                  </li>
                  <li>
                    <strong>Evidence vs heuristic</strong>: proof anchors are labeled; heuristics never appear as evidence-linked.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ACTIVATE DECISION PACKAGE (CONFIRMATION) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {isActivateDialogOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="decision-package-activate-dialog"
          onClick={() => setIsActivateDialogOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 200,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(780px, 100%)',
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: 18,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, fontFamily: 'Georgia, serif', color: c.text }}>
                  Approve & Activate decision package
                </div>
                <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
                  Evidence-linked where available. Assumptions and data gaps are shown. No changes are applied without your approval.
                </div>
                <div style={{ fontSize: 12, color: c.textMuted, marginTop: 8 }}>
                  {(() => {
                    const base = `As-of ${formatAsOfDate(data.asOf)} • ${decisionDocketKeys.length} decision${
                      decisionDocketKeys.length === 1 ? '' : 's'
                    }`;
                    if (!activationReadiness.missingSelections) return base;
                    return `${base} • ${activationReadiness.missingSelections} missing option selection`;
                  })()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsActivateDialogOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 6,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: 12, height: 12, color: c.textMuted }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: c.text }}>Close</span>
              </button>
            </div>

            <div style={{ marginTop: 14, border: `1px solid ${c.border}`, borderRadius: 10, overflow: 'hidden' }}>
              {decisionDocketKeys.map((initiativeKey, idx) => {
                const bet = data.bets.find((b) => b.key === initiativeKey) ?? null;
                const selectedOptionIndex = draftDecisionSelections[initiativeKey] ?? null;
                const selectedOption =
                  bet && selectedOptionIndex != null ? bet.options[selectedOptionIndex] ?? null : null;
                const question =
                  data.decisionDocket.find((d) => d.linkedInitiativeKey === initiativeKey)?.question ??
                  bet?.name ??
                  initiativeKey;
                const details = draftDecisionDetails[initiativeKey] ?? emptyDraftDecisionDetails();
                const ownerRaw = String(details.ownerUserId ?? '').trim();
                const ownerLabel = formatExecutiveOwnerLabel(ownerRaw, ownerCandidateLabelById);
                const dueLabel = String(details.dueDate ?? '').trim() || '—';
                const rationale = String(details.rationale ?? '').trim();
                const cadenceRaw = String(details.reviewCadence ?? '').trim().toLowerCase();
                const cadenceLabel =
                  cadenceRaw === 'weekly'
                    ? 'Weekly'
                    : cadenceRaw === 'biweekly'
                      ? 'Biweekly'
                      : cadenceRaw === 'monthly'
                        ? 'Monthly'
                        : '—';
                const successCriteria = String(details.successCriteria ?? '').trim();
                const structuredTargets = formatStructuredTargetsFromDraft(details.targets);

                return (
                  <div
                    key={initiativeKey}
                    style={{
                      padding: '12px 14px',
                      backgroundColor: idx % 2 ? c.cardMuted : c.card,
                      borderBottom: idx === decisionDocketKeys.length - 1 ? 'none' : `1px solid ${c.border}`,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: c.text, lineHeight: 1.4 }}>
                      {question}
                    </div>
                    <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4, lineHeight: 1.4 }}>
                      Selected: {selectedOption?.action ?? 'No option selected'}
                    </div>
                    <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                      Owner: {ownerLabel} • Due: {dueLabel} • Cadence: {cadenceLabel}
                    </div>
                    {structuredTargets ? (
                      <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                        Targets (metrics): {structuredTargets}
                      </div>
                    ) : null}
                    {successCriteria ? (
                      <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                        Targets (text): {successCriteria}
                      </div>
                    ) : null}
                    {rationale ? (
                      <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                        Rationale: {rationale}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setIsActivateDialogOpen(false)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  fontSize: 12,
                  fontWeight: 600,
                  color: c.text,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!onActivateDecisionPackage) return;
                  if (writeLocked === true) return;
                  if (activationReadiness.isReady !== true) return;
                  setIsActivateDialogOpen(false);
                  await onActivateDecisionPackage(draftDecisionsForApi);
                }}
                disabled={
                  writeLocked === true ||
                  !onActivateDecisionPackage ||
                  activationReadiness.isReady !== true ||
                  isDecisionPackageActive === true ||
                  isActivatingDecisionPackage === true
                }
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  backgroundColor: c.green,
                  border: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  cursor:
                    writeLocked === true ||
                    !onActivateDecisionPackage ||
                    isDecisionPackageActive === true ||
                    isActivatingDecisionPackage === true
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    writeLocked === true ||
                    !onActivateDecisionPackage ||
                    isDecisionPackageActive === true ||
                    isActivatingDecisionPackage === true
                      ? 0.7
                      : 1,
                }}
              >
                {isActivatingDecisionPackage ? 'Activating…' : 'Approve & Activate'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* REVIEW LOG (CONFIRMATION) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {isReviewDialogOpen && activationBaseline ? (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="decision-package-review-dialog"
          onClick={() => setIsReviewDialogOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 200,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(720px, 100%)',
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: 18,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, fontFamily: 'Georgia, serif', color: c.text }}>
                  Mark reviewed
                </div>
                <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
                  Logs a review event for the active decision package tied to this snapshot. No decisions change without approval.
                </div>
                <div style={{ fontSize: 12, color: c.textMuted, marginTop: 8 }}>
                  As-of {formatAsOfDate(data.asOf)} • {activationDeltaRows.length} decision{activationDeltaRows.length === 1 ? '' : 's'} • Package v{activationBaseline.version}
                </div>

                <div
                  style={{
                    marginTop: 14,
                    display: 'grid',
                    gridTemplateColumns: '0.75fr 1.25fr',
                    gap: 12,
                    alignItems: 'start',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Outcome (optional)
                    </div>
                    <select
                      value={reviewOutcome ?? ''}
                      onChange={(e) => {
                        const next = String(e.target.value ?? '').trim().toLowerCase();
                        if (!next) return setReviewOutcome(null);
                        if (next === 'continue' || next === 'escalate' || next === 'rescope' || next === 'defer') {
                          return setReviewOutcome(next as ExecutionCockpitDecisionPackageReviewRequestDto['outcome']);
                        }
                        setReviewOutcome(null);
                      }}
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '8px 10px',
                        borderRadius: 8,
                        backgroundColor: c.cardMuted,
                        border: `1px solid ${c.border}`,
                        color: c.text,
                        fontSize: 12,
                      }}
                    >
                      <option value="">Not set</option>
                      <option value="continue">Continue</option>
                      <option value="escalate">Escalate</option>
                      <option value="rescope">Re-scope</option>
                      <option value="defer">Defer</option>
                    </select>
                    <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                      Applies to all decisions in this package.
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Review notes (optional)
                    </div>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(String(e.target.value ?? ''))}
                      placeholder="Optional: decision summary, blockers, and asks."
                      rows={3}
                      maxLength={2000}
                      style={{
                        width: '100%',
                        marginTop: 6,
                        padding: '8px 10px',
                        borderRadius: 8,
                        backgroundColor: c.cardMuted,
                        border: `1px solid ${c.border}`,
                        color: c.text,
                        fontSize: 12,
                        lineHeight: 1.5,
                        resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 6 }}>
                      <div style={{ fontSize: 11, color: c.textMuted }}>
                        Stored in the decision package audit trail.
                      </div>
                      <div style={{ fontSize: 11, color: c.textMuted }}>
                        {Math.min(2000, reviewNotes.length)}/2000
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsReviewDialogOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 6,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: 12, height: 12, color: c.textMuted }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: c.text }}>Close</span>
              </button>
            </div>

            <div style={{ marginTop: 14, border: `1px solid ${c.border}`, borderRadius: 10, overflow: 'hidden' }}>
              {activationDeltaRows.map((row, idx) => {
                const betName = data.bets.find((b) => b.key === row.initiativeKey)?.name ?? row.initiativeKey;
                const decision = row.decision ?? null;
                const ownerUserId = decision?.ownerUserId ?? null;
                const ownerLabel = formatExecutiveOwnerLabel(ownerUserId, ownerCandidateLabelById);

                const cadenceRaw = String(decision?.reviewCadence ?? '').trim().toLowerCase();
                const cadenceLabel =
                  cadenceRaw === 'weekly'
                    ? 'Weekly'
                    : cadenceRaw === 'biweekly'
                      ? 'Biweekly'
                      : cadenceRaw === 'monthly'
                        ? 'Monthly'
                        : 'Not set';

                const lastReviewedIso = decision?.lastReviewedAt ?? null;
                const lastReviewedLabel = lastReviewedIso ? formatShortDate(lastReviewedIso) : 'Not logged';

                const nextReviewDueIso = activationBaseline?.activatedAt
                  ? computeNextReviewDueIso({
                      referenceAt: lastReviewedIso ?? activationBaseline.activatedAt,
                      cadence: decision?.reviewCadence ?? null,
                    })
                  : null;
                const nextReviewDueLabel = nextReviewDueIso ? formatShortDate(nextReviewDueIso) : cadenceRaw ? '—' : 'Not set';
                const isReviewOverdue = (() => {
                  if (!nextReviewDueIso) return false;
                  const ms = new Date(nextReviewDueIso).getTime();
                  if (!Number.isFinite(ms)) return false;
                  return Date.now() > ms;
                })();
                const overdueLabel = isReviewOverdue ? (lastReviewedIso ? 'Overdue' : 'Overdue (no review logged)') : null;

                return (
                  <div
                    key={row.initiativeKey}
                    style={{
                      padding: '12px 14px',
                      backgroundColor: idx % 2 ? c.cardMuted : c.card,
                      borderBottom: idx === activationDeltaRows.length - 1 ? 'none' : `1px solid ${c.border}`,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: c.text, lineHeight: 1.4 }}>
                      {betName}
                    </div>
                    <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                      Owner: {ownerLabel} • Cadence: {cadenceLabel} • Last review: {lastReviewedLabel} • Next due: {nextReviewDueLabel}
                      {overdueLabel ? <span style={{ color: c.red, fontWeight: 700 }}> • {overdueLabel}</span> : null}
                    </div>

                    {onLoadDecisionPackageReviewHistory ? (
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={async () => {
                            const key = String(row.initiativeKey ?? '').trim();
                            if (!key) return;
                            if (!onLoadDecisionPackageReviewHistory) return;
                            setReviewHistoryInitiativeKey(key);
                            setReviewHistoryStatus('loading');
                            setReviewHistoryItems([]);
                            setReviewHistoryError(null);
                            try {
                              const res = await onLoadDecisionPackageReviewHistory(key);
                              const items = Array.isArray(res?.items) ? res.items : [];
                              setReviewHistoryItems(items);
                              setReviewHistoryStatus('ready');
                            } catch (err: unknown) {
                              const message = err instanceof Error ? err.message : 'Failed to load review history.';
                              setReviewHistoryError(message);
                              setReviewHistoryStatus('error');
                            }
                          }}
                          disabled={reviewHistoryStatus === 'loading' && reviewHistoryInitiativeKey === row.initiativeKey}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            backgroundColor: c.cardMuted,
                            border: `1px solid ${c.border}`,
                            fontSize: 12,
                            fontWeight: 700,
                            color: c.text,
                            cursor:
                              reviewHistoryStatus === 'loading' && reviewHistoryInitiativeKey === row.initiativeKey
                                ? 'not-allowed'
                                : 'pointer',
                            opacity:
                              reviewHistoryStatus === 'loading' && reviewHistoryInitiativeKey === row.initiativeKey ? 0.7 : 1,
                          }}
                        >
                          {reviewHistoryStatus === 'loading' && reviewHistoryInitiativeKey === row.initiativeKey
                            ? 'Loading history…'
                            : 'View review history'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setIsReviewDialogOpen(false)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  fontSize: 12,
                  fontWeight: 600,
                  color: c.text,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!onReviewDecisionPackage) return;
                  if (writeLocked === true) return;
                  const initiativeKeys = activationDeltaRows.map((r) => r.initiativeKey).filter(Boolean);
                  const notes = String(reviewNotes ?? '').trim() || null;
                  setIsReviewDialogOpen(false);
                  await onReviewDecisionPackage({
                    initiativeKeys,
                    outcome: reviewOutcome ?? null,
                    notes,
                  });
                }}
                disabled={writeLocked === true || !onReviewDecisionPackage || isReviewingDecisionPackage === true}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  backgroundColor: c.green,
                  border: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  cursor:
                    writeLocked === true || !onReviewDecisionPackage || isReviewingDecisionPackage === true
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    writeLocked === true || !onReviewDecisionPackage || isReviewingDecisionPackage === true ? 0.7 : 1,
                }}
              >
                {isReviewingDecisionPackage ? 'Marking…' : 'Mark reviewed'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* REVIEW HISTORY (PER INITIATIVE) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {reviewHistoryInitiativeKey ? (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="decision-package-review-history-dialog"
          onClick={() => {
            setReviewHistoryInitiativeKey(null);
            setReviewHistoryStatus('idle');
            setReviewHistoryItems([]);
            setReviewHistoryError(null);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 210,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(820px, 100%)',
              maxHeight: 'min(78vh, 760px)',
              overflow: 'auto',
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: 18,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, fontFamily: 'Georgia, serif', color: c.text }}>
                  Review history
                </div>
                <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6 }}>
                  {(() => {
                    const key = String(reviewHistoryInitiativeKey ?? '').trim();
                    const betName = data.bets.find((b) => b.key === key)?.name ?? key;
                    return `Initiative: ${betName}`;
                  })()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setReviewHistoryInitiativeKey(null);
                  setReviewHistoryStatus('idle');
                  setReviewHistoryItems([]);
                  setReviewHistoryError(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 6,
                  backgroundColor: c.cardMuted,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: 12, height: 12, color: c.textMuted }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: c.text }}>Close</span>
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              {reviewHistoryStatus === 'loading' ? (
                <div style={{ fontSize: 12, color: c.textMuted }}>Loading review history…</div>
              ) : null}
              {reviewHistoryStatus === 'error' && reviewHistoryError ? (
                <div style={{ fontSize: 12, color: c.red, fontWeight: 700 }}>{reviewHistoryError}</div>
              ) : null}
              {reviewHistoryStatus === 'ready' ? (
                reviewHistoryItems.length ? (
                  <div style={{ border: `1px solid ${c.border}`, borderRadius: 10, overflow: 'hidden' }}>
                    {reviewHistoryItems.map((item, idx) => {
                      const actorRaw = item.actorUserId ?? null;
                      const actorLabel = actorRaw
                        ? ownerCandidateById.get(actorRaw)?.label ?? ownerCandidateById.get(actorRaw)?.email ?? actorRaw
                        : 'Unknown';
                      const outcomeLabel =
                        item.outcome === 'continue'
                          ? 'Continue'
                          : item.outcome === 'escalate'
                            ? 'Escalate'
                            : item.outcome === 'rescope'
                              ? 'Re-scope'
                              : item.outcome === 'defer'
                                ? 'Defer'
                                : 'Not set';
                      const versionLabel = item.packageVersion != null ? `v${item.packageVersion}` : 'v—';
                      const reviewedAsOfLabel = item.reviewedAsOf ? formatAsOfDate(item.reviewedAsOf) : '—';
                      const notes = String(item.notes ?? '').trim();
                      return (
                        <div
                          key={item.eventId}
                          style={{
                            padding: '12px 14px',
                            backgroundColor: idx % 2 ? c.cardMuted : c.card,
                            borderBottom: idx === reviewHistoryItems.length - 1 ? 'none' : `1px solid ${c.border}`,
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 700, color: c.text, lineHeight: 1.4 }}>
                            {formatAsOfDate(item.reviewedAt)} • {versionLabel} • {outcomeLabel}
                          </div>
                          <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                            Reviewed by {actorLabel} • Snapshot as-of {reviewedAsOfLabel}
                            {item.reviewedForRunId ? ' • Reviewed against approved brief' : ''}
                          </div>
                          {notes ? (
                            <div
                              style={{
                                marginTop: 10,
                                padding: '10px 12px',
                                borderRadius: 10,
                                backgroundColor: c.card,
                                border: `1px solid ${c.border}`,
                                fontSize: 12,
                                color: c.text,
                                lineHeight: 1.5,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {notes}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: c.textMuted }}>No review events logged for this initiative yet.</div>
                )
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* FOOTER */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <footer
        style={{
          borderTop: `1px solid ${c.border}`,
          backgroundColor: c.card,
          padding: '8px 20px',
          marginTop: 20,
        }}
      >
        <div
          style={{
            maxWidth: 1480,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 11,
            color: c.textMuted,
          }}
        >
          <span>
            Evidence: {evidenceCounts.connected} connected • {evidenceCounts.stale} stale • {evidenceCounts.notConnected} missing • As-of {asOfLabel}
          </span>
          <span>HELIX Strategy-to-Execution Assurance • Confidential</span>
        </div>
      </footer>
    </div>
  );
}

export default ExecutionCockpitDailyBriefV6;
